import { Database } from "./supabase.types";
import { GetLocationResponseData } from "@/app/api/getLocation/types";
import { SupabaseClient } from "@supabase/supabase-js";
import { createClientComponentClient } from "@/utils/supabase/client";
import { getHostname } from "@/components/common/utils";
import { logException } from "@/analytics/sentry";

// Given a country code in format (ISO_3166-1_alpha-2), return if it's in the EU
// https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
export function getIsEuCountry(countryCode: string | null): boolean | null {
  if (!countryCode) {
    return null;
  }
  // https://european-union.europa.eu/principles-countries-history/eu-countries_en
  const EU_COUNTRY_CODES = [
    "AT",
    "BE",
    "BG",
    "HR",
    "CY",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
  ];
  if (EU_COUNTRY_CODES.includes(countryCode)) {
    return true;
  }
  return false;
}

export async function addUserPostalCode({
  supabase,
  postalCode,
  countryCode,
  isFromIP,
  isEu,
}: {
  supabase: SupabaseClient<Database>;
  postalCode: string;
  countryCode: string | null;
  isFromIP: boolean;
  isEu: boolean | null;
}): Promise<boolean> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const user = session?.user;

  if (!user) {
    throw new Error("User must have session to save postal code");
  }

  if (!postalCode) {
    throw new Error("Postal code must be provided");
  }

  if (!countryCode) {
    throw new Error("Country code must be provided");
  }

  const { data: existingRow, error: selectError } = await supabase
    .from("user_zipcodes")
    .select("user")
    .eq("user", user.id)
    .maybeSingle();

  if (selectError) {
    logException(selectError, {
      data: "Error looking up existing zipcode row in user_zipcodes",
    });
  }

  if (existingRow) {
    // Update the zipcode for the user's UUID
    const { error: updateError } = await supabase
      .from("user_zipcodes")
      .update({
        zipcode: postalCode,
        from_ip: isFromIP,
        country_code: countryCode,
        is_eu: isEu,
      })
      .eq("user", user.id);

    if (updateError) {
      console.error("Error updating Postal Code:", updateError);
      throw new Error(
        `Error updating postal code ${postalCode} for user ${user.id}`
      );
    }
  } else {
    // Insert a new row for the user with the given zipcode
    const { error: insertError } = await supabase.from("user_zipcodes").insert({
      // FIXME - this needs to be fixed because there are ~300 users with more than one entry
      user: user.id,
      zipcode: postalCode,
      from_ip: isFromIP,
      country_code: countryCode,
      is_eu: isEu,
    });

    if (insertError) {
      logException(insertError, { data: `Error inserting Postal Code` });
      throw new Error(`Error adding postal ${postalCode} for user ${user.id}`);
    }
  }

  return true;
}
export const UNKNOWN_POSTAL_CODE = "Unknown";
export async function getIpData(): Promise<GetLocationResponseData> {
  try {
    const baseUrl = getHostname();

    // hit our own API to get the IP data
    const ipDataResponse = await fetch(`${baseUrl}/api/getLocation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!ipDataResponse.ok) {
      logException(
        `Failed to fetch IP data, status:, "${ipDataResponse.status}"`
      );
      return {
        latitude: null,
        longitude: null,
        postalCode: UNKNOWN_POSTAL_CODE,
        countryCode: null,
        isEu: null,
      }; // Fallback in case of error
    }

    const ipData: GetLocationResponseData = await ipDataResponse.json();
    return ipData;
  } catch (error) {
    logException(`Error fetching IP data "${error}"`);
    return {
      latitude: null,
      longitude: null,
      postalCode: UNKNOWN_POSTAL_CODE,
      countryCode: null,
      isEu: null,
    }; // Fallback in case of error
  }
}

export type PostalCodeData = {
  city: string | null;
  country_code: string | null;
  latitude: number | null;
  longitude: number | null;
  state: string | null;
  state_abbreviation: string | null;
  postal_code: string;
};

/**
 * Returns geographical data for a given zipcode.
 *
 * Returns lat, long, city, state, and state abbreviation.
 */
export async function lookupZipcodeData(
  zipcode: string,
  countryCode: string
): Promise<PostalCodeData | null> {
  const supabase = createClientComponentClient<Database>();

  const { data } = await supabase
    .from("postal_codes")
    .select("*")
    .eq("postal_code", zipcode)
    .eq("country_code", countryCode)
    .maybeSingle();

  if (data) {
    return data;
  }

  return null;
}

export async function checkPostalCodeExists(
  supabase: SupabaseClient<Database>,
  postalCode: string,
  countryCode: string
) {
  const { error, count } = await supabase
    .from("postal_codes")
    .select("*", { count: "exact", head: true })
    .eq("postal_code", postalCode)
    .eq("country_code", countryCode);

  if (error) {
    throw error;
  }

  if (count && count > 0) {
    return true;
  }
  return false;
}

export interface UserZipcodeData {
  zipcode: string | null;
  country_code: string | null;
}

export async function getUserZipcode(
  supabase: SupabaseClient<Database>,
  user_id?: string,
  from_ip: boolean = false,
  any_ip: boolean = false
): Promise<UserZipcodeData | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const userId = user_id ? user_id : session?.user?.id;

  // Return early if no user ID, otherwise query to user_zipcodes fails with 400
  if (!userId) {
    return null;
  }

  let query = supabase
    .from("user_zipcodes")
    .select("zipcode,country_code")
    .eq("user", userId);

  if (!any_ip) {
    query = query.eq("from_ip", from_ip);
  }

  const { data } = await query.maybeSingle();

  if (data) {
    return data;
  }

  return null;
}

/**
 * Get a user's Zip/Postal Code and Country Code
 * from their profile (if populated), otherwise fallback to IP address
 */
export async function getUserZipcodeOrFallbackToIp(
  supabase: SupabaseClient<Database>
): Promise<UserZipcodeData> {
  const userZipcode = await getUserZipcode(supabase);
  if (userZipcode) {
    return userZipcode;
  }

  const { postalCode: ipZipcode, countryCode: ipCountryCode } =
    await getIpData();

  return {
    zipcode: ipZipcode == UNKNOWN_POSTAL_CODE ? null : ipZipcode,
    country_code: ipCountryCode,
  };
}

/**
 * Get a user's location in lat/long coordinates
 * from their profile (if populated), otherwise fallback to IP address
 */
export async function getUserLocationOrFallbackToIp(
  supabase: SupabaseClient<Database>
): Promise<{
  latitude: number | null;
  longitude: number | null;
}> {
  const userZipcode = await getUserZipcode(supabase);

  if (userZipcode && userZipcode.zipcode && userZipcode.country_code) {
    const data = await lookupZipcodeData(
      userZipcode.zipcode,
      userZipcode.country_code
    );

    if (data && data.latitude && data.longitude) {
      return {
        latitude: data.latitude,
        longitude: data.longitude,
      };
    }
  }

  const { latitude: ipLatitude, longitude: ipLongitude } = await getIpData();
  return {
    latitude: ipLatitude ?? null,
    longitude: ipLongitude ?? null,
  };
}
